import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer
} from '@mui/material';
import {
  Chat as ChatIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { useSearchParams } from 'react-router-dom';
import {
  chatApi,
  ChatMessage,
  ChatThread,
  ThreadParticipant,
  ThreadDetails,
  ThreadListResponse
} from '../utils/api';
import ChatList from '../components/chat/ChatList';
import ChatWindow from '../components/chat/ChatWindow';
import EventDetailsPanel from '../components/chat/EventDetailsPanel';

const Chat: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { notifications, markAsRead } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  
  // WebSocket connection
  const { isConnected, joinThread, leaveThread, onNewMessage } = useWebSocket();
  
  // State
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [participants, setParticipants] = useState<{ [threadId: string]: ThreadParticipant[] }>({});
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<{ [threadId: string]: ChatMessage[] }>({});
  const [threadDetails, setThreadDetails] = useState<{ [threadId: string]: ThreadDetails }>({});
  
  // Loading states
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Error states
  const [error, setError] = useState<string | null>(null);
  
  // Mobile drawer state
  const [showEventDetails, setShowEventDetails] = useState(false);
  
  // Ref to store loadThreads function so it can be accessed in callbacks
  const loadThreadsRef = useRef<(() => Promise<void>) | null>(null);
  
  // Ref to track the last selected thread to avoid unnecessary reloads
  const lastSelectedThreadRef = useRef<string | null>(null);
  
  // Ref to track current selectedThreadId to avoid stale closures in loadThreads
  const selectedThreadIdRef = useRef<string | null>(null);
  
  // Refs to track current messages and threadDetails to avoid stale closures
  const messagesRef = useRef(messages);
  const threadDetailsRef = useRef(threadDetails);
  
  // Keep selectedThreadIdRef in sync
  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
  }, [selectedThreadId]);
  
  // Keep refs in sync with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  useEffect(() => {
    threadDetailsRef.current = threadDetails;
  }, [threadDetails]);

  // Handle new messages from WebSocket
  const handleNewMessage = useCallback((message: ChatMessage) => {
    console.log('📨 Received new message via WebSocket:', {
      id: message.id,
      thread_id: message.thread_id,
      sender_id: message.sender_id,
      body: message.body?.substring(0, 50)
    });
    
    setMessages(prev => {
      const threadMessages = prev[message.thread_id] || [];
      // Check if message already exists (prevent duplicates)
      // Check by ID first (most reliable), then by seq + body + sender_id + timestamp as fallback
      const messageExists = threadMessages.some(m => {
        // Check by ID first (most reliable)
        if (m.id && message.id && m.id === message.id) return true;
        // Fallback: check if same seq, body, sender, and similar timestamp (within 1 second)
        if (m.seq === message.seq && 
            m.body === message.body && 
            m.sender_id === message.sender_id) {
          const timeDiff = Math.abs(new Date(m.created_at).getTime() - new Date(message.created_at).getTime());
          if (timeDiff < 1000) return true;
        }
        return false;
      });
      
      if (messageExists) {
        console.log('⚠️ Message already exists, skipping:', message.id);
        return prev; // Don't update if message already exists
      }

      console.log('✅ Adding new message to state:', message.id);
      return {
        ...prev,
        [message.thread_id]: [
          ...threadMessages,
          message
        ].sort((a, b) => a.seq - b.seq)
      };
    });

    // Update thread's last message
    setThreads(prev => {
      const threadExists = prev.some(t => t.id === message.thread_id);
      if (!threadExists) {
        // Thread not in list yet, but we received a message - reload threads to get it
        console.log('🔄 Received message for thread not in list, reloading threads:', message.thread_id);
        // Reload threads to get the new thread (use setTimeout to avoid state update during render)
        setTimeout(() => {
          if (loadThreadsRef.current) {
            loadThreadsRef.current();
          }
        }, 0);
        return prev;
      }
      
      return prev.map(thread => 
        thread.id === message.thread_id
          ? {
              ...thread,
              last_message: {
                id: message.id,
                sender_id: message.sender_id,
                sender_name: message.sender_name,
                body: message.body,
                created_at: message.created_at,
                kind: message.kind
              },
              // Update unread count if this thread is not currently selected
              unread_count: thread.id === selectedThreadId ? 0 : (thread.unread_count || 0) + 1
            }
          : thread
      );
    });
  }, [selectedThreadId]);

  // Register message callback immediately when component mounts and WebSocket is available
  // This ensures the callback is registered before any messages arrive
  useEffect(() => {
    console.log('🔔 Registering handleNewMessage callback');
    onNewMessage(handleNewMessage);
    return () => {
      console.log('🔕 Unregistering handleNewMessage callback');
      // Cleanup: unregister callback when component unmounts
      onNewMessage(() => {});
    };
  }, [onNewMessage, handleNewMessage]);

  // Join/leave threads when selection changes
  useEffect(() => {
    if (selectedThreadId && isConnected) {
      console.log('🔗 Joining thread on selection:', selectedThreadId);
      joinThread(selectedThreadId);
      return () => {
        console.log('🔌 Leaving thread on deselection:', selectedThreadId);
        leaveThread(selectedThreadId);
      };
    } else if (selectedThreadId && !isConnected) {
      console.warn('⚠️ Cannot join thread - WebSocket not connected');
    }
  }, [selectedThreadId, isConnected, joinThread, leaveThread]);

  const loadThreads = useCallback(async () => {
    try {
      setThreadsLoading(true);
      setError(null);
      
      const response: ThreadListResponse = await chatApi.getThreads();
      setThreads(response.threads);
      setParticipants(response.participants);
      
      // Auto-select thread from URL parameter or first thread
      // IMPORTANT: Only auto-select if no thread is currently selected (don't override user's selection)
      const threadParam = searchParams.get('thread');
      const currentSelectedId = selectedThreadIdRef.current; // Use ref to get current value (avoids stale closure)
      
      if (threadParam && response.threads.find(t => t.id === threadParam)) {
        // Only set if it's different from current selection
        if (currentSelectedId !== threadParam) {
          console.log('🔀 Auto-selecting thread from URL param:', threadParam);
          setSelectedThreadId(threadParam);
        }
      } else if (!currentSelectedId && response.threads.length > 0) {
        // Only auto-select first thread if nothing is selected
        console.log('🔀 Auto-selecting first thread:', response.threads[0].id);
        setSelectedThreadId(response.threads[0].id);
      } else {
        console.log('🔀 Keeping current thread selection:', currentSelectedId);
      }
    } catch (err: any) {
      console.error('Error loading threads:', err);
      setError('Failed to load chats');
    } finally {
      setThreadsLoading(false);
    }
  }, [searchParams]); // Remove selectedThreadId from dependencies to prevent resetting selection when it changes
  
  // Store loadThreads in ref so it can be accessed in callbacks
  useEffect(() => {
    loadThreadsRef.current = loadThreads;
  }, [loadThreads]);

  // Load threads on component mount
  useEffect(() => {
    if (isAuthenticated) {
      loadThreads();
    }
  }, [isAuthenticated, loadThreads]);

  const loadMessages = useCallback(async (threadId: string) => {
    try {
      console.log('🔄 loadMessages called for thread:', threadId);
      setMessagesLoading(true);
      const threadMessages = await chatApi.getMessages(threadId);
      console.log('✅ Messages loaded from API:', threadId, 'count:', threadMessages.length);
      
      setMessages(prev => {
        // Create a completely new object to ensure React detects the change
        const newMessages = { ...prev };
        // Create a new array for this thread's messages
        newMessages[threadId] = [...threadMessages.sort((a, b) => a.seq - b.seq)];
        console.log('💾 Updating messages state for thread:', threadId, 'new count:', newMessages[threadId].length);
        console.log('💾 Messages state keys before:', Object.keys(prev));
        console.log('💾 Messages state keys after:', Object.keys(newMessages));
        console.log('💾 First message in new array:', newMessages[threadId][0]?.body?.substring(0, 30));
        return newMessages;
      });

      // Mark messages as read
      if (threadMessages.length > 0) {
        const lastMessage = threadMessages[threadMessages.length - 1];
        await chatApi.markMessagesRead(threadId, lastMessage.seq);
        
        // Update unread count in threads
        setThreads(prev => prev.map(thread => 
          thread.id === threadId ? { ...thread, unread_count: 0 } : thread
        ));
      }
    } catch (err: any) {
      console.error('❌ Error loading messages:', err);
      setError('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const loadThreadDetails = useCallback(async (threadId: string) => {
    try {
      setDetailsLoading(true);
      const details = await chatApi.getThreadDetails(threadId);
      setThreadDetails(prev => ({
        ...prev,
        [threadId]: details
      }));
    } catch (err: any) {
      console.error('Error loading thread details:', err);
      setError('Failed to load event details');
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const handleThreadSelect = useCallback((threadId: string) => {
    setSelectedThreadId(threadId);
    
    // Close mobile drawer
    if (isMobile) {
      setShowEventDetails(false);
    }
  }, [isMobile]);

  // Load messages and details when thread is selected (either manually or auto-selected)
  useEffect(() => {
    if (!selectedThreadId) {
      return;
    }
    
    // Only load if this is a different thread than last time
    if (selectedThreadId === lastSelectedThreadRef.current) {
      return;
    }
    
    console.log('🔄 Thread changed from', lastSelectedThreadRef.current, 'to', selectedThreadId);
    
    // Thread changed - clear any existing messages for this thread first to force UI update
    // This ensures the UI shows loading state immediately
    setMessages(prev => {
      const updated = { ...prev };
      // Don't clear, but ensure we have a placeholder so UI knows to show loading
      if (!updated[selectedThreadId]) {
        updated[selectedThreadId] = [];
      }
      return updated;
    });
    
    // Always load messages and details when switching threads
    // This ensures fresh data and handles edge cases where state might be stale
    console.log('📥 Loading messages for thread:', selectedThreadId);
    loadMessages(selectedThreadId);
    
    console.log('📥 Loading details for thread:', selectedThreadId);
    loadThreadDetails(selectedThreadId);

    if (!isMobile) {
      setShowEventDetails(true);
    }
    
    // Update the ref to track this as the last selected thread
    lastSelectedThreadRef.current = selectedThreadId;
  }, [selectedThreadId, loadMessages, loadThreadDetails, isMobile]);

  // Part B Option 2: When opening a thread, mark all unread notifications for that thread as read
  useEffect(() => {
    if (!selectedThreadId) return;
    const ids = notifications
      .filter((n) => n.thread_id?.trim() === selectedThreadId && !n.is_read)
      .map((n) => n.id);
    if (ids.length > 0) {
      markAsRead(ids);
    }
  }, [selectedThreadId, notifications, markAsRead]);

  const handleSendMessage = async (messageText: string) => {
    if (!selectedThreadId) return;

    try {
      const newMessage = await chatApi.sendMessage(selectedThreadId, messageText);
      
      // Add message to local state (WebSocket will also send it, but this provides immediate feedback)
      setMessages(prev => ({
        ...prev,
        [selectedThreadId]: [
          ...(prev[selectedThreadId] || []),
          newMessage
        ].sort((a, b) => a.seq - b.seq)
      }));
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="warning">
          Please log in to access your chats.
        </Alert>
      </Box>
    );
  }

  const selectedThread = threads.find(t => t.id === selectedThreadId);
  
  // Use useMemo to ensure these recompute when dependencies change
  const selectedThreadMessages = useMemo(() => {
    const msgs = selectedThreadId ? messages[selectedThreadId] || [] : [];
    console.log('🔄 Computing selectedThreadMessages:', {
      threadId: selectedThreadId,
      messagesCount: msgs.length,
      allThreadIds: Object.keys(messages),
      messagesForThisThread: selectedThreadId ? (messages[selectedThreadId]?.length || 0) : 0,
      messagesArray: msgs.slice(0, 2).map(m => ({ id: m.id, body: m.body?.substring(0, 20) }))
    });
    // Always return a new array reference to ensure React detects changes
    return [...msgs];
  }, [selectedThreadId, messages]);
  
  const selectedThreadDetails = useMemo(() => {
    return selectedThreadId ? threadDetails[selectedThreadId] : undefined;
  }, [selectedThreadId, threadDetails]);
  
  const selectedThreadParticipants = useMemo(() => {
    return selectedThreadId ? participants[selectedThreadId] || [] : [];
  }, [selectedThreadId, participants]);
  
  // Debug logging
  useEffect(() => {
    if (selectedThreadId) {
      console.log('📋 Current state for selected thread:', {
        threadId: selectedThreadId,
        messagesCount: selectedThreadMessages.length,
        hasDetails: !!selectedThreadDetails,
        messagesArray: selectedThreadMessages.slice(0, 3).map(m => m.body?.substring(0, 20))
      });
    }
  }, [selectedThreadId, selectedThreadMessages, selectedThreadDetails]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 2, zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ChatIcon sx={{ color: '#533B7D', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
            Messages
          </Typography>
          {!isConnected && (
            <Alert severity="warning" sx={{ ml: 'auto' }}>
              Connecting...
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Chat List */}
          <Grid 
            item 
            xs={12} 
            md={4} 
            lg={3}
            sx={{ 
              height: '100%',
              borderRight: { md: '1px solid #e0e0e0' },
              display: { xs: selectedThreadId ? 'none' : 'block', md: 'block' }
            }}
          >
            <ChatList
              threads={threads}
              participants={participants}
              selectedThreadId={selectedThreadId || undefined}
              onThreadSelect={handleThreadSelect}
              loading={threadsLoading}
            />
          </Grid>

          {/* Chat Window */}
          <Grid 
            item 
            xs={12} 
            md={showEventDetails ? 5 : 8} 
            lg={showEventDetails ? 6 : 9}
            sx={{ 
              height: '100%',
              display: { xs: selectedThreadId ? 'block' : 'none', md: 'block' }
            }}
          >
            {selectedThreadId ? (
              <Box sx={{ height: '100%', position: 'relative' }}>
                {/* Mobile back button and event details toggle */}
                {isMobile && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8, 
                    zIndex: 10,
                    display: 'flex',
                    gap: 1
                  }}>
                    <IconButton
                      onClick={() => setShowEventDetails(true)}
                      sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                    >
                      <InfoIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => setSelectedThreadId(null)}
                      sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}

                {/* Desktop event details toggle */}
                {!isMobile && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8, 
                    zIndex: 10 
                  }}>
                    <IconButton
                      onClick={() => setShowEventDetails(!showEventDetails)}
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        color: showEventDetails ? '#533B7D' : 'inherit'
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Box>
                )}

                {(() => {
                  console.log('🎯 Rendering ChatWindow with:', {
                    threadId: selectedThreadId,
                    messagesCount: selectedThreadMessages.length,
                    loading: messagesLoading,
                    firstMessage: selectedThreadMessages[0]?.body?.substring(0, 30)
                  });
                  return null;
                })()}
                <ChatWindow
                  key={`${selectedThreadId}-${selectedThreadMessages.length}`} // Force remount when thread or message count changes
                  threadId={selectedThreadId}
                  messages={selectedThreadMessages}
                  threadDetails={selectedThreadDetails}
                  loading={messagesLoading}
                  error={error || undefined}
                  onSendMessage={handleSendMessage}
                />
              </Box>
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#f5f5f5'
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <ChatIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a chat to start messaging
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>

          {/* Event Details Panel - Desktop */}
          {!isMobile && showEventDetails && selectedThreadDetails && (
            <Grid item md={3} lg={3} sx={{ height: '100%' }}>
              <EventDetailsPanel
                threadDetails={selectedThreadDetails}
                participants={selectedThreadParticipants}
                onClose={() => setShowEventDetails(false)}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Event Details Drawer - Mobile */}
      {isMobile && (
        <Drawer
          anchor="right"
          open={showEventDetails}
          onClose={() => setShowEventDetails(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '90%',
              maxWidth: 400
            }
          }}
        >
          {selectedThreadDetails && (
            <EventDetailsPanel
              threadDetails={selectedThreadDetails}
              participants={selectedThreadParticipants}
              onClose={() => setShowEventDetails(false)}
            />
          )}
        </Drawer>
      )}

      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 1000 }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default Chat;
